import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { designService } from '../../services/api/design.service';
import { trackEvent } from '../../utils/analytics';
import { XMarkIcon, SparklesIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

interface ReturningUserWelcomeProps {
  onClose: () => void;
}

/**
 * Returning User Welcome Modal
 * 
 * Shows after login from checkout - displays user's designs and past orders
 * Helps users feel recognized and see their account value
 */
export const ReturningUserWelcome: React.FC<ReturningUserWelcomeProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        // Fetch user's designs
        const userDesigns = await designService.getUserDesigns();
        setDesigns(userDesigns.slice(0, 3)); // Show only first 3
        
        trackEvent('checkout.returning_user_welcome_shown', {
          designCount: userDesigns.length,
        });
      } catch (error) {
        console.error('Failed to load user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between bg-gradient-to-r from-purple-50 to-blue-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">👋 Welcome Back!</h2>
            <p className="text-sm text-gray-600 mt-1">
              Great to see you again
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Designs Section */}
              {designs.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <SparklesIcon className="h-5 w-5 text-purple-600" />
                    <h3 className="font-semibold text-gray-900">Your Designs</h3>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {designs.map((design) => (
                      <div
                        key={design.id}
                        className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-purple-400 transition-colors cursor-pointer"
                        onClick={() => {
                          navigate(`/design/${design.id}/customize`);
                          onClose();
                        }}
                      >
                        {design.frontPreviewUrl ? (
                          <img
                            src={design.frontPreviewUrl}
                            alt={design.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <SparklesIcon className="h-8 w-8" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => {
                      navigate('/account/designs');
                      onClose();
                    }}
                    className="text-sm text-purple-600 hover:text-purple-700 underline"
                  >
                    View all designs →
                  </button>
                </div>
              )}

              {/* Continue Checkout CTA */}
              <div className="pt-4 border-t">
                <button
                  onClick={onClose}
                  className="w-full px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <ShoppingBagIcon className="h-5 w-5 inline mr-2" />
                  Continue to Checkout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};





